import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Chip, Typography, Button, LinearProgress, Stack, CircularProgress,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import { useFaceLandmarker } from '@/hooks/useFaceLandmarker';
import { evaluateFrame } from '@/components/faceAnalysis/qualityChecks';
import { POSES, TOTAL_POSES } from '@/components/faceAnalysis/poses';

const HOLD_MS = 800;       // must hold a valid pose this long before capture
const COOLDOWN_MS = 1100;  // pause after a capture so the client can change face
const MANUAL_AFTER_MS = 15000; // if stuck on a pose this long, allow manual proceed
const CAPTURE_N = 448;     // captured photos are standardized to N×N pixels

/**
 * Standardize a video frame to an N×N square WITHOUT distortion: scale to fit
 * (preserve aspect), centered, padding the remainder. Landmarks (normalized to
 * the source frame) are remapped into the same N×N space so the stored map
 * still lines up with the stored image. Keeps the upload small + uniform —
 * 448×448 JPEG ≈ 35–65 KB, so the ten poses together stay well under a megabyte.
 *
 * @returns {{ image:string, landmarks:Array<{x,y,z}>, imageSize:{width:number,height:number} }}
 */
function frameToSquare(video, landmarks, N) {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  let image = '';
  if (vw && vh) {
    const scale = N / Math.max(vw, vh);
    const dw = Math.round(vw * scale);
    const dh = Math.round(vh * scale);
    const dx = Math.round((N - dw) / 2);
    const dy = Math.round((N - dh) / 2);
    try {
      const c = document.createElement('canvas');
      c.width = N;
      c.height = N;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, N, N);
      ctx.drawImage(video, 0, 0, vw, vh, dx, dy, dw, dh);
      image = c.toDataURL('image/jpeg', 0.82);
    } catch { /* image is best-effort */ }
  }
  // Remap normalized landmarks into the padded N×N square (uniform transform).
  const s = vw && vh ? 1 / Math.max(vw, vh) : 0; // source-pixel → N-normalized
  const dxN = vw && vh ? (N - Math.round(vw * (N / Math.max(vw, vh)))) / 2 / N : 0;
  const dyN = vw && vh ? (N - Math.round(vh * (N / Math.max(vw, vh)))) / 2 / N : 0;
  const mapped = landmarks.map((p) => ({
    x: p.x * vw * s + dxN,
    y: p.y * vh * s + dyN,
    z: p.z * vw * s,
  }));
  return { image, landmarks: mapped, imageSize: { width: N, height: N } };
}

/**
 * Live capture flow. Walks the four POSES in order. For each, it waits until
 * all framing/lighting checks pass AND the expression is detected, holds
 * briefly to avoid false triggers, then captures the LANDMARK MAP (plus an
 * on-device thumbnail used only for review — never uploaded).
 *
 * The <video> + overlay canvas are ALWAYS mounted; permission/loading states
 * render as overlays on top. This is required so the camera stream has an
 * element to attach to the moment it's acquired.
 *
 * @param {{ onComplete: (captures:Array) => void }} props
 */
export default function FaceCaptureFlow({ onComplete }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);

  const poseIndexRef = useRef(0);
  const holdStartRef = useRef(0);
  const cooldownUntilRef = useRef(0);
  const capturesRef = useRef([]);
  const doneRef = useRef(false);

  // Manual-proceed support: when the operator is stuck on a pose for too long,
  // a button appears to capture WITHOUT the expression check — but it still
  // requires a well-positioned face (see handleManualCapture / button disabled).
  const phaseStartRef = useRef(0);       // when the current pose phase began
  const manualShownRef = useRef(false);  // guard so we flip the flag once per pose
  const latestFrameRef = useRef(null);   // last good frame data for manual capture
  const mirrorRef = useRef(true);        // mirror preview/overlay only for the front cam

  const [poseIndex, setPoseIndex] = useState(0);
  const [qualityStatus, setQualityStatus] = useState('noFace');
  const [holdProgress, setHoldProgress] = useState(0);
  const [justCaptured, setJustCaptured] = useState(null);
  const [manualAvailable, setManualAvailable] = useState(false);

  /** Draw the mesh overlay; mirror only when the preview is mirrored (front cam). */
  const drawOverlay = useCallback((video, landmarks, ok, mirror) => {
    const canvas = canvasRef.current;
    if (!canvas || !video.videoWidth) return;
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
    }
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!landmarks || landmarks.length === 0) return;

    ctx.save();
    if (mirror) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.fillStyle = ok ? 'rgba(20,184,166,0.85)' : 'rgba(217,119,6,0.85)';
    const r = Math.max(1, canvas.width / 520);
    for (let i = 0; i < landmarks.length; i += 1) {
      const p = landmarks[i];
      ctx.beginPath();
      ctx.arc(p.x * canvas.width, p.y * canvas.height, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }, []);

  const capture = useCallback((pose, landmarks, categories, headPose, video) => {
    // Standardize the frame to N×N (small, uniform) and remap the landmark map
    // into the same space so image + map stay aligned. The image is sent to the
    // server and shown on the review screen.
    const { image, landmarks: squareLandmarks, imageSize } = frameToSquare(video, landmarks, CAPTURE_N);

    capturesRef.current.push({
      pose: pose.key,
      landmarks: squareLandmarks.map((p) => ({ x: p.x, y: p.y, z: p.z })),
      blendshapes: categories.map((cat) => ({ name: cat.categoryName, score: cat.score })),
      headPose,
      imageSize,
      // Whether the operator saw a mirrored (selfie/front) preview — the review
      // thumbnail flips to match only when this is true.
      mirrored: mirrorRef.current,
      capturedAt: new Date().toISOString(),
      image,
    });

    setJustCaptured(pose.key);
    cooldownUntilRef.current = performance.now() + COOLDOWN_MS;
    holdStartRef.current = 0;

    // Restart the stuck-timer for the next pose and hide the manual button.
    phaseStartRef.current = performance.now();
    manualShownRef.current = false;
    setManualAvailable(false);

    const next = poseIndexRef.current + 1;
    if (next >= TOTAL_POSES) {
      doneRef.current = true;
      setTimeout(() => onComplete(capturesRef.current), 700);
    } else {
      poseIndexRef.current = next;
      setPoseIndex(next);
    }
  }, [onComplete]);

  const handleFrame = useCallback((result, video) => {
    if (doneRef.current) return;

    const faces = result.faceLandmarks || [];
    const faceCount = faces.length;
    const landmarks = faces[0] || [];
    const matrix = result.facialTransformationMatrixes?.[0]?.data || [];
    const categories = result.faceBlendshapes?.[0]?.categories || [];

    // Skip the frontal gate for head-angle poses (laterals, up/down); presence,
    // framing and lighting still apply.
    const pose = POSES[poseIndexRef.current];
    const { status, headPose } = evaluateFrame({
      video, landmarks, faceCount, matrix,
      requireFrontal: pose.requireFrontal !== false,
    });

    if (performance.now() < cooldownUntilRef.current) {
      drawOverlay(video, landmarks, true, mirrorRef.current);
      return;
    }
    setJustCaptured(null);

    const scoreMap = Object.create(null);
    for (const c of categories) scoreMap[c.categoryName] = c.score;
    const score = (name) => scoreMap[name] || 0;

    const poseOk = status === null && pose.verify(score, headPose);

    // Stash the latest frame so the manual-proceed button can capture it.
    latestFrameRef.current = { landmarks, categories, headPose, video };

    // After MANUAL_AFTER_MS stuck on this pose, reveal the manual button.
    if (phaseStartRef.current === 0) phaseStartRef.current = performance.now();
    if (!manualShownRef.current
        && performance.now() - phaseStartRef.current > MANUAL_AFTER_MS) {
      manualShownRef.current = true;
      setManualAvailable(true);
    }

    drawOverlay(video, landmarks, poseOk, mirrorRef.current);
    setQualityStatus(status);

    if (poseOk) {
      if (!holdStartRef.current) holdStartRef.current = performance.now();
      const held = performance.now() - holdStartRef.current;
      setHoldProgress(Math.min(1, held / HOLD_MS));
      if (held >= HOLD_MS) capture(pose, landmarks, categories, headPose, video);
    } else {
      holdStartRef.current = 0;
      setHoldProgress(0);
    }
  }, [capture, drawOverlay]);

  const { videoRef, status, start, facingMode, canSwitch, switchCamera } =
    useFaceLandmarker({ onFrame: handleFrame, active: true });

  // Mirror the preview/overlay/review only for the front (selfie) camera.
  const mirror = facingMode === 'user';
  useEffect(() => { mirrorRef.current = mirror; }, [mirror]);

  useEffect(() => {
    if (status === 'ready') {
      setHoldProgress(0);
      setJustCaptured(null);
      // Begin the stuck-timer for the first pose.
      phaseStartRef.current = performance.now();
      manualShownRef.current = false;
      setManualAvailable(false);
    }
  }, [status]);

  /**
   * Manual proceed: capture the current pose WITHOUT the expression check, but
   * ONLY when the face is still well-positioned/present (qualityStatus === null,
   * which means in-boundary, frontal, single face, decent lighting). This skips
   * pose recognition, never the face-presence/positioning guarantees.
   */
  function handleManualCapture() {
    if (qualityStatus !== null) return;          // require a good, in-boundary face
    const f = latestFrameRef.current;
    if (!f || !f.landmarks?.length) return;
    capture(POSES[poseIndexRef.current], f.landmarks, f.categories, f.headPose, f.video);
  }

  const pose = POSES[poseIndex];
  const poseName = t(`face.poses.${pose.key}.name`);
  const instruction = t(`face.poses.${pose.key}.instruction`);
  const ready = status === 'ready';
  const errorState = ['denied', 'no-camera', 'insecure', 'error'].includes(status);

  const statusChip = (() => {
    if (justCaptured) {
      return (
        <Chip color="success" icon={<CheckCircleIcon />}
          label={t('face.captured', { name: t(`face.poses.${justCaptured}.name`) })} />
      );
    }
    if (qualityStatus === null) {
      return <Chip color="success" label={holdProgress > 0 ? t('face.holding') : t('face.quality.good')} />;
    }
    return <Chip color="warning" label={t(`face.quality.${qualityStatus}`)} />;
  })();

  return (
    <Box>
      <Box
        sx={{
          position: 'relative', width: '100%', aspectRatio: '3 / 4',
          maxHeight: '60vh', mx: 'auto', borderRadius: 3, overflow: 'hidden',
          bgcolor: 'black',
        }}
      >
        {/* Video + overlay canvas are ALWAYS mounted so the stream can attach. */}
        <video
          ref={videoRef}
          muted
          playsInline
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transform: mirror ? 'scaleX(-1)' : 'none',
            opacity: errorState ? 0 : 1,
          }}
        />
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        />

        {/* Oval framing guide + status chip — only when the camera is live. */}
        {ready && (
          <>
            <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{
                width: '62%', height: '78%', border: '3px dashed',
                borderColor: qualityStatus === null ? 'primary.light' : 'rgba(255,255,255,0.6)',
                borderRadius: '50%', transition: 'border-color 0.2s',
              }} />
            </Box>
            <Box sx={{ position: 'absolute', top: 12, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
              {statusChip}
            </Box>
            {canSwitch && (
              <IconButton
                onClick={switchCamera}
                aria-label={t('face.switchCamera')}
                sx={{
                  position: 'absolute', bottom: 12, right: 12,
                  bgcolor: 'rgba(0,0,0,0.5)', color: '#fff',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <CameraswitchIcon />
              </IconButton>
            )}
          </>
        )}

        {/* Overlay: requesting camera / loading model */}
        {(status === 'idle' || status === 'requesting' || status === 'loading') && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, p: 2,
            color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', textAlign: 'center',
          }}>
            <CircularProgress color="inherit" />
            <Typography>
              {status === 'loading' ? t('face.loadingModel') : t('face.permission.requesting')}
            </Typography>
          </Box>
        )}

        {/* Overlay: error / permission states */}
        {errorState && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 2, p: 3,
            bgcolor: 'background.paper', textAlign: 'center',
          }}>
            <VideocamOffIcon color="error" sx={{ fontSize: 56 }} />
            <Typography color="text.secondary" sx={{ maxWidth: 360 }}>
              {status === 'denied' && t('face.permission.denied')}
              {status === 'no-camera' && t('face.permission.noCamera')}
              {status === 'insecure' && t('face.permission.insecure')}
              {status === 'error' && t('face.error')}
            </Typography>
            {status !== 'insecure' && (
              <Button variant="contained" onClick={start}>{t('face.permission.retry')}</Button>
            )}
          </Box>
        )}
      </Box>

      <LinearProgress
        variant="determinate"
        value={holdProgress * 100}
        sx={{ mt: 1.5, height: 6, borderRadius: 3, opacity: ready && holdProgress > 0 ? 1 : 0.25 }}
      />

      {/* Manual proceed — appears only after being stuck on a pose too long.
          Placed right under the camera (above the pose text) so it's easy to
          reach. Skips the expression check, but stays disabled until the face
          is well-positioned (qualityStatus === null). */}
      {ready && manualAvailable && (
        <Stack spacing={0.5} sx={{ mt: 1.5, textAlign: 'center' }}>
          <Button
            variant="contained"
            onClick={handleManualCapture}
            disabled={qualityStatus !== null}
          >
            {t('face.manualProceed')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {qualityStatus === null ? t('face.manualReady') : t('face.manualHint')}
          </Typography>
        </Stack>
      )}

      <Stack spacing={0.5} sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={800}>{poseName}</Typography>
        <Typography color="text.secondary">{t('face.doExpression', { instruction })}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('face.progress', { done: capturesRef.current.length, total: TOTAL_POSES })}
        </Typography>
      </Stack>
    </Box>
  );
}
